// File: src/lib/services/userPreferences.ts
import api from '../axios';

export interface MobileNavigationItem {
  key: string;
  show: boolean;
}

export interface QuickActionItem {
  key: string;
  show: boolean;
  order: number;
}

export interface UIPreferences {
  mobileNavigation?: {
    items: MobileNavigationItem[];
    maxItems: number;
  };
  quickActions?: {
    items: QuickActionItem[];
    maxItems: number;
  };
  // Dashboard "Quick Actions" card customization (ordered action ids).
  // Empty = role-based defaults. Distinct from quickActions (mobile menu pins).
  dashboardQuickActions?: string[];
  theme?: 'light' | 'dark' | 'system';
  // Per-user dismissal of the dashboard "set up leader coaching" nudge
  hideLeadersSetupNudge?: boolean;
  documentation?: {
    defaultDateFilter?: 'all' | '7' | '30' | '60' | '90' | '180' | '365';
    defaultViewMode?: 'card' | 'list';
    defaultSortBy?: 'date' | 'name';
    defaultSortOrder?: 'asc' | 'desc';
    defaultCategoryFilter?: 'all' | 'disciplinary' | 'pip' | 'administrative';
    highlightingThresholds?: {
      excessiveDocumentationCount: number; // Orange highlighting threshold
      redCardTriggers: string[];            // Which actions trigger red cards
    };
  };
}

export interface FeatureAccessPreferences {
  fohTasks: boolean;
  setups: boolean;
  kitchen: boolean;
  documentation: boolean;
  evaluations: boolean;
  training: boolean;
  leadership: boolean;
}

export interface DashboardPreferences {
  quickStats: {
    fohTasks: boolean;
    kitchen: boolean;
    documentation: boolean;
    evaluations: boolean;
    training: boolean;
    leadership: boolean;
  };
  detailCards: {
    fohTasks: boolean;
    kitchen: boolean;
    documentation: boolean;
    evaluations: boolean;
    training: boolean;
    leadership: boolean;
  };
  listCards: {
    pendingEvaluations: boolean;
    pendingSelfEvaluations: boolean;
    pending360Evaluations: boolean;
  };
}

export interface NotificationPreferences {
  email: {
    enabled: boolean;
    evaluationScheduled: boolean;
    evaluationCompleted: boolean;
    evaluationReminder: boolean;
  };
  inApp: {
    enabled: boolean;
    evaluationScheduled: boolean;
    evaluationCompleted: boolean;
    evaluationReminder: boolean;
  };
}

export interface UserPreferences {
  _id?: string;
  user: string;
  uiPreferences?: UIPreferences;
  notificationPreferences?: NotificationPreferences;
  featureAccess?: FeatureAccessPreferences;
  dashboardPreferences?: DashboardPreferences;
  pinnedLeaders?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export const userPreferencesService = {
  getUserPreferences: async (): Promise<UserPreferences> => {
    try {
      const response = await api.get('/api/user-preferences');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUserPreferences: async (data: Partial<UserPreferences>): Promise<UserPreferences> => {
    try {
      const response = await api.patch('/api/user-preferences', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetUserPreferences: async (): Promise<UserPreferences> => {
    try {
      const response = await api.post('/api/user-preferences/reset');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Helper method to update mobile navigation preferences
  updateMobileNavigation: async (items: MobileNavigationItem[]): Promise<UserPreferences> => {
    try {
      return await userPreferencesService.updateUserPreferences({
        uiPreferences: {
          mobileNavigation: {
            items,
            maxItems: 5
          }
        }
      });
    } catch (error) {
      throw error;
    }
  },

  // Helper method to update quick action preferences
  updateQuickActions: async (items: QuickActionItem[]): Promise<UserPreferences> => {
    try {
      return await userPreferencesService.updateUserPreferences({
        uiPreferences: {
          quickActions: {
            items,
            maxItems: 6
          }
        }
      });
    } catch (error) {
      throw error;
    }
  },

  // Helper method to update the dashboard Quick Actions card selection
  updateDashboardQuickActions: async (actionIds: string[]): Promise<UserPreferences> => {
    try {
      return await userPreferencesService.updateUserPreferences({
        uiPreferences: {
          dashboardQuickActions: actionIds
        }
      });
    } catch (error) {
      throw error;
    }
  },

  // Helper method to get pinned leaders
  getPinnedLeaders: async (): Promise<string[]> => {
    try {
      const preferences = await userPreferencesService.getUserPreferences();
      return preferences.pinnedLeaders || [];
    } catch (error) {
      throw error;
    }
  },

  // Helper method to update pinned leaders
  updatePinnedLeaders: async (leaderIds: string[]): Promise<UserPreferences> => {
    try {
      return await userPreferencesService.updateUserPreferences({
        pinnedLeaders: leaderIds
      });
    } catch (error) {
      throw error;
    }
  }
};
